// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { DATA_CATEGORIES_AFFECTED, NOTIFICATION_STATUSES, POTENTIAL_CONSEQUENCES, } from '../types/incident.js';
import { ORG_IDENTITY_FIELD_DEFINITIONS } from './org-identity.js';
export const FORM_PREFILL_SOURCE_FAMILIES = [
    'organisation',
    'dpo',
    'dpia',
    'transfer',
    'incident',
    'dossier',
];
/** The risk classes a DPIA risk row carries (`RiskClass`). */
const RISK_CLASSES = ['low', 'medium', 'high', 'critical'];
/**
 * The risk classes by name, as the product labels them in both languages
 * (`vi.ts` risk levels: Thấp, Trung bình, Cao, Nghiêm trọng).
 */
const RISK_CLASS_LABELS = {
    low: { en: 'Low', vi: 'Thấp' },
    medium: { en: 'Medium', vi: 'Trung bình' },
    high: { en: 'High', vi: 'Cao' },
    critical: { en: 'Critical', vi: 'Nghiêm trọng' },
};
/**
 * The data categories an impact assessment records (`DataCategoryAffected`),
 * by name in both languages. Sprint 63.37.0 (legal second read S12): Mẫu-style
 * fields ask for "the types of personal data concerned" in words, and the
 * platform holds these codes. The Vietnamese for the sensitive and children's
 * categories is the wording of the personal-data law ("dữ liệu cá nhân nhạy
 * cảm", "dữ liệu cá nhân của trẻ em"); the others are the product's
 * (`vi.ts` consent purpose categories).
 */
const DATA_CATEGORY_LABELS = {
    personal_data: { en: 'Personal data', vi: 'Dữ liệu cá nhân' },
    sensitive_personal_data: { en: 'Sensitive personal data', vi: 'Dữ liệu cá nhân nhạy cảm' },
    financial_data: { en: 'Financial data', vi: 'Dữ liệu tài chính' },
    health_data: { en: 'Health data', vi: 'Dữ liệu sức khỏe' },
    childrens_data: { en: "Children's personal data", vi: 'Dữ liệu cá nhân của trẻ em' },
    other: { en: 'Other personal data', vi: 'Dữ liệu cá nhân khác' },
};
/**
 * The incident page's potential consequences as words. The source reads
 * POTENTIAL consequences: no official form field that asks for consequences
 * that occurred may declare it.
 */
const POTENTIAL_CONSEQUENCE_LABELS = {
    identity_theft: { en: 'Identity theft', vi: 'Bị đánh cắp danh tính' },
    financial_loss: { en: 'Financial loss', vi: 'Tổn thất tài chính' },
    reputational_damage: { en: 'Reputational damage', vi: 'Tổn hại uy tín' },
    discrimination: { en: 'Discrimination', vi: 'Phân biệt đối xử' },
    emotional_distress: { en: 'Emotional distress', vi: 'Tổn thương tinh thần' },
    physical_harm: { en: 'Physical harm', vi: 'Tổn hại thể chất' },
    loss_of_confidentiality: { en: 'Loss of confidentiality', vi: 'Mất tính bảo mật' },
    loss_of_access: { en: 'Loss of access to the data', vi: 'Mất quyền truy cập dữ liệu' },
};
/** A DPIA can be read directly, or through a dossier filing that names it. */
const DPIA_MODULES = ['dpia', 'dossier_filing'];
/** A transfer assessment can be read directly, or through a dossier filing that names it. */
const TRANSFER_MODULES = ['transfer', 'dossier_filing'];
/** An incident can be read directly, or through the breach notification raised on it. */
const INCIDENT_MODULES = ['incident', 'breach_notification'];
const DOSSIER_MODULES = ['dossier_filing'];
const ORGANISATION_SOURCES = ORG_IDENTITY_FIELD_DEFINITIONS.map((d) => ({
    source: `organisation.${d.field}`,
    family: 'organisation',
    value_type: d.kind === 'date' ? 'date' : 'text',
    modules: 'all',
}));
export const FORM_PREFILL_SOURCES = [
    ...ORGANISATION_SOURCES,
    // The in-effect appointment in the DPO appointment register.
    { source: 'dpo.full_name', family: 'dpo', value_type: 'text', modules: 'all' },
    { source: 'dpo.position_title', family: 'dpo', value_type: 'text', modules: 'all' },
    { source: 'dpo.email', family: 'dpo', value_type: 'text', modules: 'all' },
    { source: 'dpo.phone', family: 'dpo', value_type: 'text', modules: 'all' },
    // DPIA — the record, its risks and its mitigations.
    { source: 'dpia.purpose', family: 'dpia', value_type: 'text', modules: DPIA_MODULES },
    { source: 'dpia.data_types', family: 'dpia', value_type: 'list', modules: DPIA_MODULES },
    { source: 'dpia.data_subjects', family: 'dpia', value_type: 'list', modules: DPIA_MODULES },
    { source: 'dpia.risks', family: 'dpia', value_type: 'list', modules: DPIA_MODULES },
    {
        source: 'dpia.risk_level_max',
        family: 'dpia',
        value_type: 'enum',
        modules: DPIA_MODULES,
        enum_values: RISK_CLASSES,
        enum_labels: RISK_CLASS_LABELS,
    },
    {
        source: 'dpia.residual_risk_level_max',
        family: 'dpia',
        value_type: 'enum',
        modules: DPIA_MODULES,
        enum_values: RISK_CLASSES,
        enum_labels: RISK_CLASS_LABELS,
    },
    { source: 'dpia.technical_measures', family: 'dpia', value_type: 'list', modules: DPIA_MODULES },
    {
        source: 'dpia.organisational_measures',
        family: 'dpia',
        value_type: 'list',
        modules: DPIA_MODULES,
    },
    { source: 'dpia.assessor_name', family: 'dpia', value_type: 'text', modules: DPIA_MODULES },
    { source: 'dpia.approver_name', family: 'dpia', value_type: 'text', modules: DPIA_MODULES },
    { source: 'dpia.approved_at', family: 'dpia', value_type: 'datetime', modules: DPIA_MODULES },
    { source: 'dpia.legal_basis', family: 'dpia', value_type: 'list', modules: DPIA_MODULES },
    { source: 'dpia.retention', family: 'dpia', value_type: 'list', modules: DPIA_MODULES },
    {
        source: 'dpia.first_filing_at',
        family: 'dpia',
        value_type: 'datetime',
        modules: DPIA_MODULES,
    },
    {
        source: 'dpia.change_description',
        family: 'dpia',
        value_type: 'text',
        modules: DPIA_MODULES,
    },
    {
        source: 'dpia.change_effective_at',
        family: 'dpia',
        value_type: 'datetime',
        modules: DPIA_MODULES,
    },
    // Cross-border transfer assessment and its safeguards.
    {
        source: 'transfer.recipient_name',
        family: 'transfer',
        value_type: 'text',
        modules: TRANSFER_MODULES,
    },
    {
        source: 'transfer.destination_country',
        family: 'transfer',
        value_type: 'country',
        modules: TRANSFER_MODULES,
    },
    { source: 'transfer.purpose', family: 'transfer', value_type: 'text', modules: TRANSFER_MODULES },
    {
        source: 'transfer.data_types',
        family: 'transfer',
        value_type: 'list',
        modules: TRANSFER_MODULES,
    },
    {
        source: 'transfer.data_subjects',
        family: 'transfer',
        value_type: 'list',
        modules: TRANSFER_MODULES,
    },
    {
        source: 'transfer.risk_narrative',
        family: 'transfer',
        value_type: 'text',
        modules: TRANSFER_MODULES,
    },
    {
        source: 'transfer.safeguards',
        family: 'transfer',
        value_type: 'list',
        modules: TRANSFER_MODULES,
    },
    {
        source: 'transfer.mechanism',
        family: 'transfer',
        value_type: 'text',
        modules: TRANSFER_MODULES,
    },
    // Incident, read directly or through the breach notification raised on it.
    {
        source: 'incident.discovered_at',
        family: 'incident',
        value_type: 'datetime',
        modules: INCIDENT_MODULES,
    },
    {
        source: 'incident.description',
        family: 'incident',
        value_type: 'text',
        modules: INCIDENT_MODULES,
    },
    {
        source: 'incident.data_categories',
        family: 'incident',
        value_type: 'list',
        modules: INCIDENT_MODULES,
        enum_values: DATA_CATEGORIES_AFFECTED,
        enum_labels: DATA_CATEGORY_LABELS,
    },
    {
        source: 'incident.subjects_affected',
        family: 'incident',
        value_type: 'range',
        modules: INCIDENT_MODULES,
    },
    {
        source: 'incident.consequences',
        family: 'incident',
        value_type: 'list',
        modules: INCIDENT_MODULES,
        enum_values: POTENTIAL_CONSEQUENCES,
        enum_labels: POTENTIAL_CONSEQUENCE_LABELS,
    },
    {
        source: 'incident.containment_measures',
        family: 'incident',
        value_type: 'list',
        modules: INCIDENT_MODULES,
    },
    {
        source: 'incident.remediation_measures',
        family: 'incident',
        value_type: 'list',
        modules: INCIDENT_MODULES,
    },
    {
        source: 'incident.subject_notification_status',
        family: 'incident',
        value_type: 'enum',
        modules: INCIDENT_MODULES,
        enum_values: NOTIFICATION_STATUSES,
    },
    // Dossier filing and the dossier materialised from it.
    { source: 'dossier.reference', family: 'dossier', value_type: 'text', modules: DOSSIER_MODULES },
    {
        source: 'dossier.receiving_authority',
        family: 'dossier',
        value_type: 'text',
        modules: DOSSIER_MODULES,
    },
];
/** Every source name, in registry order. */
export const FORM_PREFILL_SOURCE_NAMES = FORM_PREFILL_SOURCES.map((d) => d.source);
/**
 * The organisation identity sources — the list a census reads to decide
 * whether an official form's identity field is declared (E2).
 */
export const FORM_PREFILL_IDENTITY_SOURCES = ORGANISATION_SOURCES.map((d) => d.source);
const BY_NAME = new Map(FORM_PREFILL_SOURCES.map((d) => [d.source, d]));
/** The registry entry for a source name, or `undefined` for a name it does not hold. */
export function getFormPrefillSource(name) {
    return BY_NAME.get(name);
}
/**
 * Whether a source resolves for a form embedded in `moduleType` (or a
 * standalone form, `null`). Only `'all'` sources resolve for a standalone form.
 */
export function formPrefillSourceAppliesTo(definition, moduleType) {
    if (definition.modules === 'all')
        return true;
    return moduleType !== null && definition.modules.includes(moduleType);
}
/**
 * The parent module type an instance's `parent_entity_type` names. The
 * transfer register is written `transfer` by the orchestration service and
 * `transfer_assessment` by its list page; both are the same record. Any other
 * parent type (a vendor, a programme) has no record-scoped source, so it maps
 * to `null` and only the organisation and DPO sources apply.
 */
export function formPrefillModuleOf(parentEntityType) {
    switch (parentEntityType) {
        case 'dpia':
        case 'breach_notification':
        case 'authority_request':
        case 'dossier_filing':
        case 'incident':
            return parentEntityType;
        case 'transfer':
        case 'transfer_assessment':
            return 'transfer';
        default:
            return null;
    }
}
//# sourceMappingURL=form-prefill-sources.js.map